import { useEffect } from 'react';
import { useCanvasStore } from '../store/canvas-store';
import { invalidateCanvas } from '../render/canvasInvalidate';
import type { ThemeToken } from '../store/types';

const LINK_ID = 'cf-theme-fonts';

// Families we must never request from Google — generic / system stacks.
const GENERIC = new Set([
  'sans-serif', 'serif', 'monospace', 'system-ui', 'ui-sans-serif', 'ui-serif',
  'ui-monospace', 'cursive', 'fantasy', 'inherit', 'initial', 'unset',
  '-apple-system', 'blinkmacsystemfont',
]);

/** First family in a CSS font stack, unquoted; null if it's a generic/system family. */
export function primaryFamily(stack: string | undefined): string | null {
  if (!stack) return null;
  const first = stack.split(',')[0].trim().replace(/^['"]|['"]$/g, '').trim();
  if (!first || GENERIC.has(first.toLowerCase())) return null;
  return first;
}

/**
 * Build a Google Fonts CSS2 href for every non-generic family referenced by the
 * themes' typography, requesting the weights the themes actually use (plus 400 &
 * 700 as safe defaults). Returns null when no web font is referenced.
 */
export function googleFontsHref(themes: ThemeToken[]): string | null {
  const families = new Map<string, Set<number>>();
  for (const t of themes) {
    // Partial/agent-generated/migrated themes may omit the typography group.
    for (const ty of Object.values(t.typography ?? {})) {
      const fam = primaryFamily(ty.fontFamily);
      if (!fam) continue;
      const weights = families.get(fam) ?? new Set<number>([400, 700]);
      const w = Number(ty.fontWeight);
      if (Number.isFinite(w)) weights.add(w);
      families.set(fam, weights);
    }
  }
  if (families.size === 0) return null;
  const parts = [...families.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([fam, weights]) => {
      const ws = [...weights].sort((a, b) => a - b).join(';');
      const name = encodeURIComponent(fam).replace(/%20/g, '+');
      return `family=${name}:wght@${ws}`;
    });
  return `https://fonts.googleapis.com/css2?${parts.join('&')}&display=swap`;
}

/**
 * Inject/update a single <link> loading the Google Fonts referenced by the
 * canvas themes, and repaint the html-in-canvas once they finish loading.
 * App-controlled (fonts.googleapis.com), so it bypasses the per-frame external-
 * resource stripping that applies to untrusted frame HTML.
 */
export function useThemeFonts(): void {
  const themes = useCanvasStore((s) => s.doc.themes);
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const href = googleFontsHref(themes ?? []);
    let link = document.getElementById(LINK_ID) as HTMLLinkElement | null;
    if (!href) {
      link?.remove();
      return;
    }
    if (!link) {
      link = document.createElement('link');
      link.id = LINK_ID;
      link.rel = 'stylesheet';
      document.head.appendChild(link);
    }
    if (link.href !== href) link.href = href;
    // html-in-canvas paints to a bitmap, so it won't reflow when fonts load
    // asynchronously — force one repaint when they're ready.
    document.fonts?.ready.then(() => invalidateCanvas()).catch(() => {});
  }, [themes]);
}
